import type { PlanItem } from '../../data/domains/subscription';
import { catalogPlanMatchesSelectedPortion, getCatalogPlanPortion } from '../../data/domains/subscription/catalog/subscriptionPortion';

export interface PremiumCategoryItem {
  id: string | number;
  title: string;
  arabic_title?: string;
  attachment?: string;
  subscriptions?: any[];
}

export function buildPremiumCategories(regular_plans: any[]): PremiumCategoryItem[] {
  return regular_plans.map(cat => ({
    id: cat.id ?? cat.title ?? '',
    title: cat.title ?? 'Plan',
    arabic_title: cat.arabic_title,
    attachment: cat.attachment ?? cat.thumbnail ?? cat.image ?? cat.banner ?? cat.cover,
    subscriptions: cat.subscriptions ?? [],
  }));
}

/**
 * Day buckets for this category that have at least one active plan matching the selected member count.
 */
export function buildDurationsForCategory(category: PremiumCategoryItem | null, selected_portion: number): number[] {
  if (!category?.subscriptions?.length) return [];
  const set = new Set<number>();
  for (const sub of category.subscriptions) {
    const d = Number(sub.days ?? sub.type_key ?? sub.duration ?? 0);
    if (d <= 0) continue;
    const type_data = sub.type_data ?? [];
    const has_plan_for_portion = type_data.some(
      (plan: unknown) => (plan as { is_active?: boolean })?.is_active !== false && catalogPlanMatchesSelectedPortion(plan as Record<string, unknown>, selected_portion),
    );
    if (has_plan_for_portion) set.add(d);
  }
  return Array.from(set).sort((a, b) => a - b);
}

export function buildPlanItemsForCategoryAndDuration(
  category: PremiumCategoryItem | null,
  duration: number,
  selected_portion: number,
): PlanItem[] {
  if (!category?.subscriptions?.length || duration <= 0) return [];
  const items: PlanItem[] = [];
  const cat_title = category.title ?? 'Plan';
  const cat_image = category.attachment;

  for (const sub of category.subscriptions) {
    const day_val = Number(sub.days ?? sub.type_key ?? sub.duration ?? 0);
    if (day_val !== duration) continue;
    const type_data = sub.type_data ?? [];
    for (const plan of type_data) {
      if (plan.is_active === false) continue;
      if (!catalogPlanMatchesSelectedPortion(plan as Record<string, unknown>, selected_portion)) continue;
      const portion_key = String(getCatalogPlanPortion(plan as Record<string, unknown>) ?? 'default');
      items.push({
        key: `plan-${category.id}-${day_val}-${plan.subscription_id ?? plan.id}-${portion_key}`,
        plan: { ...plan, days: day_val, type_key: day_val, duration: day_val },
        days: day_val,
        categoryTitle: cat_title,
        categoryImage: plan.thumbnail ?? plan.attachment ?? cat_image,
      });
    }
  }
  return items;
}

/** i18n `t()` has no interpolation — replace `{{amount}}` safely. */
export function formatSubscriptionFooterTotal(t: (key: string) => string, amount_display: string): string {
  const raw = t('subscriptions.totalWithAmount');
  if (raw.includes('{{amount}}')) {
    return raw.split('{{amount}}').join(amount_display);
  }
  return `${raw} ${amount_display}`.trim();
}

export const CATEGORY_STRIP_GAP = 12;

export function getCategoryBannerSize(screen_width: number): { width: number; height: number } {
  const width = Math.min(300, Math.max(232, screen_width - 56));
  const height = Math.round(width * 0.62);
  return { width, height };
}
